/**
 * SetViewToggle - Toggle between table, grid, and summary view modes for set details
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';
import { useAtom } from 'jotai';
import { FaAlignLeft, FaGripHorizontal, FaList } from 'react-icons/fa';

import { setViewModeAtom, type SetViewMode } from '../state/setDetailsAtom';

export interface SetViewToggleProps {
  className?: string;
}

interface ViewOption {
  mode: SetViewMode;
  icon: ReactElement;
  label: string;
}

const VIEW_OPTIONS: ViewOption[] = [
  { mode: 'table', icon: <FaList className="h-3.5 w-3.5" />, label: 'Table view' },
  { mode: 'grid', icon: <FaGripHorizontal className="h-3.5 w-3.5" />, label: 'Grid view' },
  { mode: 'summary', icon: <FaAlignLeft className="h-3.5 w-3.5" />, label: 'Summary view' },
];

export default function SetViewToggle({ className }: SetViewToggleProps): ReactElement {
  const [viewMode, setViewMode] = useAtom(setViewModeAtom);

  return (
    <div className={clsx('inline-flex rounded-lg border border-zinc-700 bg-zinc-800/50 p-0.5', className)}>
      {VIEW_OPTIONS.map(({ mode, icon, label }) => (
        <button
          key={mode}
          type="button"
          onClick={() => setViewMode(mode)}
          className={clsx(
            'flex items-center justify-center rounded-md px-3 py-1.5 transition-all',
            'focus-visible:ring-aether focus-visible:ring-offset-void focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
            viewMode === mode ? 'bg-zinc-700 text-zinc-100 shadow-sm' : 'text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200',
          )}
          aria-label={label}
          aria-pressed={viewMode === mode}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}
